/**
 * Iter 188.46 · Phase 3 — Word-paste sanitizer for the Proposal Builder.
 *
 * Pasting from Microsoft Word into a browser `contentEditable` region
 * dumps a huge amount of Office-specific cruft: `mso-*` inline styles,
 * `<o:p>` XML tags, class="MsoNormal" wrappers, `<style>` blocks with
 * hundreds of rules, deprecated `<font>` tags, border attributes, and
 * fixed-width table `style="width: 240pt"` declarations that break
 * responsive layout.
 *
 * We keep only structural tags (headings, lists, tables, paragraphs,
 * inline formatting), strip every attribute except the whitelisted
 * few, and coerce style-based bold/italic to semantic `<strong>` /
 * `<em>` so downstream PDF rendering stays predictable.
 *
 * Reference: the mockup's `cleanPaste()` function. Ported verbatim
 * (and extended for edge cases) to keep the same behavior the design
 * team validated against Word 2016 / 365, Google Docs, and plain HTML.
 *
 * This module is pure — no DOM globals, no React. Tests use jsdom's
 * DOMParser (Jest's default env).
 */

// Structural + formatting tags we keep. Everything else is unwrapped.
const _KEEP_TAGS = new Set([
  "p", "br", "div", "span",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li",
  "strong", "b", "em", "i", "u", "s", "strike", "sub", "sup",
  "table", "thead", "tbody", "tfoot", "tr", "td", "th", "caption",
  "a", "blockquote", "hr",
]);

// Attributes we keep per tag. Everything else — including all
// `mso-*` styles, `class`, `id`, `xml:*`, `lang`, `dir`, inline
// `style` on non-anchor elements — is stripped.
const _KEEP_ATTRS = {
  a: new Set(["href", "title", "target", "rel"]),
  td: new Set(["colspan", "rowspan"]),
  th: new Set(["colspan", "rowspan"]),
};

// Style-derived semantic remaps. If the raw element has one of these
// styles set to a matching value, we wrap the children in the
// semantic tag instead of relying on the inline style.
const _STYLE_SEMANTIC_REMAPS = [
  { attr: "font-weight",  matcher: (v) => /^(bold|[6-9]\d\d)$/i.test(v), wrap: "strong" },
  { attr: "font-style",   matcher: (v) => /italic|oblique/i.test(v),     wrap: "em" },
  { attr: "text-decoration", matcher: (v) => /underline/i.test(v),        wrap: "u" },
];

/**
 * Take Word/Docs/HTML clipboard content and return sanitized HTML
 * safe to store on a quote's proposal_sections. Input can be a raw
 * string (from `event.clipboardData.getData("text/html")`) or an
 * already-parsed HTMLElement (tests). Output is always a string.
 */
export function cleanPaste(input) {
  const html = typeof input === "string" ? input : (input?.outerHTML || "");
  if (!html) return "";
  // Strip whole <style>, <meta>, <script>, <o:p>, <link> subtrees
  // BEFORE parsing — this dodges DOMParser's "throw everything in body"
  // behavior and keeps the fragment small.
  const preTrimmed = html
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<meta[^>]*>/gi, "")
    .replace(/<link[^>]*>/gi, "")
    .replace(/<o:p[\s\S]*?<\/o:p>/gi, "")
    .replace(/<o:p[^>]*\/?>/gi, "")
    .replace(/<xml[\s\S]*?<\/xml>/gi, "");

  // Parse into a doc fragment. In jsdom + browser this is safe;
  // scripts don't execute in DOMParser output.
  const doc = new DOMParser().parseFromString(
    `<body>${preTrimmed}</body>`,
    "text/html",
  );
  const body = doc.body;
  _walk(body);
  // Coalesce trailing empty paragraphs / stray `&nbsp;` runs from Word.
  return body.innerHTML
    .replace(/(&nbsp;|\u00A0){2,}/g, " ")
    .replace(/<p>\s*(&nbsp;|\s)*<\/p>/gi, "")
    .replace(/(<br\s*\/?>){3,}/gi, "<br/><br/>")
    .trim();
}

function _walk(node) {
  // Iterate over a static snapshot of children — we may replace
  // subtrees during the walk.
  const kids = Array.from(node.childNodes);
  for (const child of kids) {
    if (child.nodeType === 3 /* text */) continue;
    if (child.nodeType !== 1 /* element */) {
      child.remove();
      continue;
    }
    const tag = child.tagName.toLowerCase();
    // MsoNormal / class-based unwrap: drop Word's classes but keep children.
    if (child.className && /Mso/i.test(child.className)) {
      child.removeAttribute("class");
    }
    // Style-derived semantic wraps — must happen BEFORE we strip style.
    _applyStyleSemantics(child);

    if (!_KEEP_TAGS.has(tag)) {
      // Unknown tag → replace with its children (unwrap), then walk
      // each moved child so nested unknown tags get stripped too.
      const parent = child.parentNode;
      const moved = [];
      while (child.firstChild) {
        moved.push(child.firstChild);
        parent.insertBefore(child.firstChild, child);
      }
      parent.removeChild(child);
      for (const m of moved) {
        if (m.nodeType === 1) _walk(m.parentNode ? m : m); // no-op guard
      }
      // Re-walk the parent to pick up any unknown tags now surfaced.
      _walk(parent);
      return;
    }

    // Strip all attributes except the whitelist for this tag.
    const keep = _KEEP_ATTRS[tag] || new Set();
    for (const attr of Array.from(child.attributes)) {
      if (!keep.has(attr.name.toLowerCase())) child.removeAttribute(attr.name);
    }
    // Coerce `<b>` → `<strong>` and `<i>` → `<em>` for semantic clarity
    // and downstream PDF rendering (ReportLab reads `<strong>`/`<em>`).
    if (tag === "b" || tag === "i") {
      const newTag = tag === "b" ? "strong" : "em";
      const wrap = doc_ownerOf(child).createElement(newTag);
      while (child.firstChild) wrap.appendChild(child.firstChild);
      child.parentNode.replaceChild(wrap, child);
      _walk(wrap);
      continue;
    }
    _walk(child);
  }
}

function doc_ownerOf(el) {
  return el.ownerDocument || document;
}

function _applyStyleSemantics(el) {
  const style = el.getAttribute && el.getAttribute("style");
  if (!style) return;
  const wraps = [];
  for (const { attr, matcher, wrap } of _STYLE_SEMANTIC_REMAPS) {
    const re = new RegExp(`${attr}\\s*:\\s*([^;]+)`, "i");
    const m = style.match(re);
    if (m && matcher(m[1].trim())) wraps.push(wrap);
  }
  if (wraps.length === 0) return;
  // Nest the wraps innermost-first so DOM order is `<strong><em>text</em></strong>`.
  let inner = doc_ownerOf(el).createDocumentFragment();
  while (el.firstChild) inner.appendChild(el.firstChild);
  let current = inner;
  for (const w of wraps) {
    const wrapEl = doc_ownerOf(el).createElement(w);
    wrapEl.appendChild(current);
    current = wrapEl;
  }
  el.appendChild(current);
}

/**
 * Serialize a proposal `sections` array to the payload shape we send
 * to `POST /api/quotes`. Trims titles, forces `order` to be a
 * contiguous 0..N-1 range, and drops any section that has neither a
 * title nor visible HTML content (prevents blank rows from bloating
 * the record + PDF).
 */
export function serializeSections(sections) {
  if (!Array.isArray(sections)) return [];
  return sections
    .map((s) => ({
      id: s.id || _uid(),
      title: (s.title || "").trim(),
      html: (s.html || "").trim(),
    }))
    .filter((s) => s.title || _stripTags(s.html))
    .map((s, i) => ({ ...s, order: i }));
}

function _uid() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* fallthrough */ }
  return `ps-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function _stripTags(html) {
  return String(html || "").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, "").trim();
}
